<template>
  <button
    :is="type"
    :href="isLink ? href : null"
    :type="submit ? 'submit' : null"
    :class="['button', themeClass, state, classes]"
    :aria-label="isLink && target ? $t('open_in_new_tab') : null"
    :role="isLink ? 'button' : null"
    :target="isLink ? (target ? '_blank' : null) : null"
    :style="{ backgroundColor: backgroundColor, color: color }"
  >
    <slot />
    <template v-if="secondary">
      <IconChevronSm
        v-if="size === 'small' || small"
        aria-hidden="true"
        class="icon"
      />
      <IconChevron
        v-else
        aria-hidden="true"
        class="icon"
      />
    </template>
  </button>
</template>

<script>
import i18n from '../i18n'
import IconChevron from '../assets/icons/chevron.svg'
import IconChevronSm from '../assets/icons/chevronSm.svg'
/**
 * `Buttons` are generally used for interface actions. Suitable for all-purpose use.
 * Default appearance set to `primary` style.
 * `primary` style will be used only once per view for main call-to-action.
 */
export default {
  name: 'Button',
  components: {
    IconChevron,
    IconChevronSm
  },
  props: {
    /**
     * Use dark theme for component.
     */
    dark: {
      type: Boolean,
      default: false
    },
    /**
     * If set, overrides the default light/dark theme's color for the button background color.
     */
    backgroundColor: {
      type: String,
      default: null
    },
    /**
     * If set, overrides the default light/dark theme's text color for the button.
     */
    color: {
      type: String,
      default: null
    },
    /**
     * If button’s type is a link, use this option to set a href.
     */
    href: {
      type: String,
      default: null
    },
    /**
     * Use the secondary styling for the button
     */
    secondary: {
      type: Boolean,
      default: false
    },
    /**
     * Use small size button.
     */
    small: {
      type: Boolean,
      default: false
    },
    /**
     * Manually trigger various states of the button.
     * @values hover, active, focus
     */
    state: {
      type: String,
      default: null,
      validator: value => {
        return value.match(/(hover|focus|active|visited|disabled)/)
      }
    },
    /**
     * Set the button’s type to “submit”.
     */
    submit: {
      type: Boolean,
      default: false
    },
    /**
     * Open in new window. Defaults to null.
     */
    target: {
      type: Boolean,
      default: false
    },
    /**
     * The html element used for the button.
     * @values button, a
     */
    type: {
      type: String,
      default: 'button',
      validator: value => {
        return value.match(/(button|a)/)
      }
    }
  },
  computed: {
    classes() {
      return {
        'button-primary': !this.secondary,
        'button-secondary': this.secondary,
        [`button-${(this.small | this.size === 'small') ? 'small' : 'large'}`]: true
      }
    },
    isLink() {
      return this.type === 'a'
    },
    // TODO - can be refactored out of all components as shared module once we are using Vue 3 composition API
    // TODO - can also break out things like size props + classes, etc.
    themeClass() {
      return {
        'theme-light': !this.dark,
        'theme-dark': this.dark
      }
    }
  },
  i18n
}
</script>

<style lang="scss" scoped>
$button-font-size-s: 14px;
$button-line-height-s: 24px;
$button-letter-spacing-s: 2.1px;

.button {
  @include reset;
  will-change: transform;
  transition: all 0.2s ease;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-weight: $weight-semi-bold;
  font-size: $size-m;
  font-family: $font-heading;
  @include lhCrop(1.2);
  text-transform: uppercase;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: $radius-default;
  cursor: pointer;

}

.button,
.button-primary {
  @include themify($themes) {
    color: themed('text-secondary');
    background-color: themed('primary');
    box-shadow: none;
    border: none;
    &:hover,
    &.hover {
      color: themed('text-emphasis');
      background-color: themed('emphasis');
      box-shadow: none;
    }
    &:focus,
    &.focus {
      background-color: themed('emphasis');
      color: themed('text-emphasis');
      box-shadow: 0 0 0 4px $blue-lighter;
      outline: none;
    }
    &:active,
    &.active,
    &:visited,
    &.visited {
      transition: none;
      background-color: themed('active');
      color: $white;
      box-shadow: themed('button-primary-shadow-pressed');
    }
    &:disabled,
    &.disabled {
      background-color: transparent;
      color: themed('inactive-text');
      box-shadow: themed('button-primary-shadow-inactive');
    }
  }
}
.button-secondary {
  @include themify($themes) {
    color: themed('primary');
    background-color: themed('active-secondary');
    box-shadow: themed('button-secondary-shadow-default');
    border: none;
    &:hover,
    &.hover {
      color: themed('emphasis-secondary');
      background-color: $white;
      box-shadow: themed('button-secondary-shadow-hover');
    }
    &:focus,
    &.focus {
      background-color: themed('emphasis');
      color: themed('text-emphasis-secondary');
      box-shadow: 0 0 0 4px $blue-lighter;
      outline: none;
    }
    &:active,
    &.active {
      transition: none;
      background-color: themed('active-secondary');
      color: themed('secondary');
      box-shadow: themed('button-secondary-shadow-pressed');
    }
    &:disabled,
    &.disabled {
      background-color: transparent;
      color: themed('inactive-text');
      box-shadow: themed('button-secondary-shadow-inactive');
    }
  }
  text-align: left;
  .icon {
    padding-left: 8px;
    overflow: visible;
  }
}
.button-small {
  font-size: $button-font-size-s;
  padding: $space-xs $space-m;
  letter-spacing: $button-letter-spacing-s;
  line-height: $button-line-height-s;
}
.button-large {
  font-size: 16px;
  padding: $space-s $space-m;
  letter-spacing: 2.4px;
  line-height: $line-height-m;
}
</style>
